









































import Vue from 'vue'
import { Component, Watch } from 'vue-property-decorator'
import { ageTable } from '../age-table'
import { profileText } from '../profile-text'

function round (input: number) {
  return Math.round(input * 100) / 100
}

interface IInput {
  name: string,
  age: number,
  height: number,
  weight?: number,
  bust: number,
  weist: number,
  hip: number,
}

@Component
export default class WeightMeter extends Vue {
  public input: IInput = {
    name: '',
    age: 18,
    height: 158,
    weight: undefined,
    bust: 82.16,
    weist: 63.20,
    hip: 90.06,
  }

  public twttrInitializing = true

  public get shareURL () {
    const query = Buffer.from((Object.keys(this.input) as Array<keyof IInput>).reduce((prev: string[], key) => {
      if (!this.input[key]) {
        return prev
      }
      switch (key) {
      case 'bust':
        return [...prev, `B=${this.input[key]}`]
      case 'weist':
        return [...prev, `W=${this.input[key]}`]
      case 'hip':
        return [...prev, `H=${this.input[key]}`]
      }
      return [...prev, `${key}=${this.input[key]}`]
    }, []).join('&')).toString('base64')
    return `https://eure.mobi/nijigen-taijukei/?${query}`
  }

  public get shareText () {
    return `${this.input.name ? this.input.name : '（名無し）'}さんの身長・体重・スリーサイズを二次元キャラ体重計で見てみると…？
#二次元キャラ体重計
`
  }

  public get output () {
    return [{
      name: '身長',
      input: round(this.input.height),
      standard: round(this.standardHeight),
      ideal: '-',
    }, {
      name: '体重',
      input: this.weight,
      standard: round(this.standardWeight),
      ideal: round(this.idealWeight),
    }, {
      name: this.input.age < 18 ? 'ローレル指数' : 'BMI',
      input: this.input.age < 18 ? this.rohrerIndex : this.bmi,
      standard: this.input.age < 18 ? 126.87 : 20.73,
      ideal: this.input.age < 18 ? 122.24 : 20,
    }, {
      name: 'B',
      input: `${round(this.input.bust)} (ub: ${round(this.underBustFromInput)})`,
      standard: `${round(this.standardBust)} (ub: ${round(this.standardUnderbust)})`,
      ideal: `${round(this.idealBust)} (ub: ${round(this.idealUnderbust)})`,
    }, {
      name: 'W',
      input: round(this.input.weist),
      standard: round(this.standardWeist),
      ideal: round(this.idealWeist),
    }, {
      name: 'H',
      input: round(this.input.hip),
      standard: round(this.standardHip),
      ideal: round(this.idealHip),
    }, {
      name: 'カップ数',
      input: this.cup(this.underBustFromInput, this.input.bust),
      standard: this.cup(this.standardUnderbust, this.standardBust),
      ideal: this.cup(this.idealUnderbust, this.idealBust),
    }]
  }

  public get weight () {
    return this.input.weight ? `${round(this.input.weight)} (${round(this.inferredWeight)})`
                             : round(this.inferredWeight)
  }

  public get bmi () {
    if (this.input.weight) {
      const bmi = this.input.weight / Math.pow(this.input.height / 100, 2.5) * 1.3
      return `${round(bmi)} (${round(this.inferredBMI)})`
    } else {
      return round(this.inferredBMI)
    }
  }

  public get rohrerIndex () {
    if (this.input.weight) {
      const rohrerIndex = this.input.weight / Math.pow(this.input.height / 1000, 3) / 100
      return `${round(rohrerIndex)} (${round(this.inferredRohrerIndex)})`
    } else {
      return round(this.inferredRohrerIndex)
    }
  }

  public get idealWeight () {
    return Math.pow(this.input.height / 100, 2.5) / 1.3 * 20
  }

  public get idealBust () {
    const ratioToIdealBust = (ageTable[Math.min(this.input.age, 17) - 6].ratioToBust / 0.52) * 0.53
    return this.input.height * ratioToIdealBust
  }

  public get idealUnderbust () {
    const ratioToIdealUnderbust = (this.weistIndex(this.idealWeist, this.input.height) * 0.38)
    return this.input.height * ratioToIdealUnderbust
  }

  public get idealWeist () {
    const ratioToIdealweist = (ageTable[Math.min(this.input.age, 17) - 6].ratioToWeist / 0.40) * 0.38
    return this.input.height * ratioToIdealweist
  }

  public get idealHip () {
    const ratioToIdealHip = (ageTable[Math.min(this.input.age, 17) - 6].ratioToHip / 0.57) * 0.54
    return this.input.height * ratioToIdealHip
  }

  public get standardHeight () {
    return ageTable[Math.min(this.input.age, 17) - 6].height
  }

  public get standardWeight () {
    return this.input.age < 18 ? Math.pow(this.input.height / 1000, 3) * 12687
                               : Math.pow(this.input.height / 100, 2.5) / 1.3 * 20.73
  }

  public get standardBust () {
    return this.input.height * ageTable[Math.min(this.input.age, 17) - 6].ratioToBust
  }

  public get standardUnderbust () {
    const weistIndex = this.weistIndex(this.standardWeist, this.input.height)
    return this.input.height * (weistIndex * ageTable[Math.min(this.input.age, 17) - 6].ratioToWeist)
  }

  public get standardWeist () {
    return this.input.height * ageTable[Math.min(this.input.age, 17) - 6].ratioToWeist
  }

  public get standardHip () {
    return this.input.height * ageTable[Math.min(this.input.age, 17) - 6].ratioToHip
  }

  public weistIndex (weist: number, height: number) {
    return (1.125 - Math.log2(weist / (height * 0.4)) / 10)
  }

  public get underBustFromInput () {
    const weistIndex = this.weistIndex(this.input.weist, this.input.height)
    return weistIndex * (this.input.weist / this.input.height) * this.input.height
  }

  public get ratioToStandardBust () {
    return this.input.bust / this.standardBust
  }

  public get ratioToStandardUnderbust () {
    return this.underBustFromInput / this.standardUnderbust
  }

  public get ratioToStandardWeist () {
    return this.input.weist / this.standardWeist
  }

  public get ratioToStandardHip () {
    return this.input.hip / this.standardHip
  }

  public get inferredWeight () {
    return this.input.age < 18 ? Math.pow(this.input.height / 1000, 3) * this.inferredRohrerIndex * 100
                               : Math.pow(this.input.height / 100, 2.5) / 1.3 * this.inferredBMI
  }

  public get inferredBMI () {
    const bust = this.ratioToStandardBust
    const underbust = this.ratioToStandardUnderbust
    const weist = this.ratioToStandardWeist
    const hip = this.ratioToStandardHip
    return (((bust + underbust + weist + hip) / 4 - 1) * 1.108 + 1) * 20.73
  }

  public get inferredRohrerIndex () {
    const bust = this.ratioToStandardBust
    const underbust = this.ratioToStandardUnderbust
    const weist = this.ratioToStandardWeist
    const hip = this.ratioToStandardHip
    return (((bust + underbust + weist + hip) / 4 - 1) * 1.108 + 1) * 126.87
  }

  public get heightText () {
    const model = ageTable[Math.min(this.input.age, 17) - 6]
    if (this.input.height < this.standardHeight - model.heightSD * 2) {
      return profileText.tiny
    } else if (this.input.height < this.standardHeight - model.heightSD) {
      return profileText.small
    } else if (this.input.height > this.standardHeight + model.heightSD * 2) {
      return profileText.veryTall
    } else if (this.input.height > this.standardHeight + model.heightSD) {
      return profileText.tall
    } else {
      return profileText.averageHeight
    }
  }

  public get bustText () {
    const cupIndex = this.cupIndex(this.underBustFromInput, this.input.bust)
    if (cupIndex < 4) {
      return profileText.flatTits
    } else if (cupIndex < 6) {
      return profileText.smallTits
    } else if (cupIndex > 10) {
      return profileText.hugeTits
    } else if (cupIndex > 8) {
      return profileText.bigTits
    } else {
      return profileText.averageTits
    }
  }

  public get weistText () {
    const model = ageTable[Math.min(this.input.age, 17) - 6]
    if (this.input.weist < this.standardWeist - model.weistSD * 2) {
      return profileText.skinnyWeist
    } else if (this.input.weist < this.standardWeist - model.weistSD) {
      return profileText.slimWeist
    } else if (this.input.weist > this.standardWeist + model.weistSD * 2) {
      return profileText.chubbyWeist
    } else if (this.input.weist > this.standardWeist + model.weistSD) {
      return profileText.curvyWeist
    } else {
      return profileText.averageWeist
    }
  }

  public get hipText () {
    const model = ageTable[Math.min(this.input.age, 17) - 6]
    if (this.input.hip < this.standardHip - model.hipSD * 2) {
      return profileText.flatButt
    } else if (this.input.hip < this.standardHip - model.hipSD) {
      return profileText.smallButt
    } else if (this.input.hip > this.standardHip + model.hipSD * 2) {
      return profileText.hugeButt
    } else if (this.input.hip > this.standardHip + model.hipSD) {
      return profileText.bigButt
    } else {
      return profileText.averageHip
    }
  }

  public get bodyTypeText () {
    const model = ageTable[Math.min(this.input.age, 17) - 6]
    const cupIndex = this.cupIndex(this.underBustFromInput, this.input.bust)
    const bmi = this.inferredBMI
    if (bmi <= 19) {
      if (this.input.height > this.standardHeight + model.heightSD) {
        return 'のモデル体型で'
      }
    }
    if (cupIndex > 7) {
      if (this.input.height < this.standardHeight - model.heightSD) {
        return 'のトランジスタグラマーで'
      }
    }
    if (bmi <= 20.5) {
      return 'の均整の取れた体型で'
    }
    if (bmi < 24 && bmi >= 22.5)  {
      return '健康的な肉付きの体型で'
    }
    return 'をしており'
  }

  public get profile () {
    return `${this.heightText}${this.weistText}${this.bodyTypeText}、${this.bustText}と${this.hipText}を持つ。`
  }

  public underbustForCup (underbust: number) {
    return Math.round(underbust / 5) * 5
  }

  public cupIndex (underbust: number, b: number) {
    return Math.round((b - this.underbustForCup(underbust)) / 2.5)
  }

  public cup (underbust: number, bust: number) {
    const underbustForCup = this.underbustForCup(underbust)
    const cupIndex = this.cupIndex(underbust, bust)
    const cup = cupIndex <= 2 ? 'AAA' : cupIndex <= 3 ? 'AA' : String.fromCharCode(cupIndex + 61)
    return `${cup}${underbustForCup}`
  }

  public callAfterTwitterInitialization (callback: () => void) {
    if (typeof window.twttr !== 'undefined' && typeof window.twttr.widgets !== 'undefined') {
      callback()
    } else {
      setTimeout(() => {
        this.callAfterTwitterInitialization(callback)
      }, 100)
    }
  }

  public mounted () {
    const queryPairs = Buffer.from(location.search.substring(1), 'base64').toString().split('&')
    const queryParameters: { [key: string]: string } = queryPairs.reduce((prev, queryPair) => {
      const [key, value] = queryPair.split('=').map((raw) => decodeURIComponent(raw))
      if (
        key !== 'name' &&
        key !== 'age' &&
        key !== 'height' &&
        key !== 'weight' &&
        key !== 'B' &&
        key !== 'W' &&
        key !== 'H'
      ) {
        return prev
      }
      return { ...prev, [key]: value }
    }, {})
    if (queryParameters.name) {
      this.input.name = queryParameters.name
    }
    if (queryParameters.age) {
      const age = parseInt(queryParameters.age, 10)
      if (age && 6 <= age) {
        this.input.age = age
      }
    }
    if (queryParameters.height) {
      const height = parseFloat(queryParameters.height)
      if (height) {
        this.input.height = height
      }
    }
    if (queryParameters.weight) {
      const weight = parseFloat(queryParameters.weight)
      if (weight) {
        this.input.weight = weight
      }
    }
    if (queryParameters.B) {
      const bust = parseFloat(queryParameters.B)
      if (bust) {
        this.input.bust = bust
      }
    }
    if (queryParameters.W) {
      const weist = parseFloat(queryParameters.W)
      if (weist) {
        this.input.weist = weist
      }
    }
    if (queryParameters.H) {
      const hip = parseFloat(queryParameters.H)
      if (hip) {
        this.input.hip = hip
      }
    }

    this.callAfterTwitterInitialization(() => {
      this.createShareButton()
    })
  }

  public async createShareButton () {
    const targetId = 'twitter-share-button'
    const shareButton = document.getElementById(targetId)
    if (shareButton) {
      shareButton.innerHTML = ''
    }
    await window.twttr.widgets.createShareButton(
      this.shareURL,
      document.getElementById(targetId),
      {
        lang: 'ja',
        count: 'none',
        text: this.shareText,
        size: 'large',
      })
    this.twttrInitializing = false
  }

  @Watch('shareURL')
  public onChangeShareURL () {
    if (this.twttrInitializing) {
      return
    }
    this.createShareButton()
  }
}
